import DOMPurify from 'dompurify';
import React, { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import { useHistory, useParams } from 'react-router-dom';
import rehypeRaw from 'rehype-raw'; // IMPORTANT: DO NOT USE WITHOUT DOM-PURIFY. This could lead to script injection.
import remarkGfm from 'remark-gfm';

import { Button, Card, CloseIcon, Dialog, Flex, ProviderConsumer } from '@fluentui/react-northstar';
import { Input } from 'adaptivecards';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { appState, useSetState } from '../../AppState';
import { ScopeErrorMessage } from '../../Rbac/ScopePermissionNoAccess';
import { Breadcrumbs } from '../../shared/components/BreadcrumbNavigation';
import { FileProps } from '../../shared/components/EditTicketAttachments/EditTicketAttachments';
import { NewAttachmentFlowContent } from '../../shared/components/EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent';
import { InitialPageLoader } from '../../shared/components/InitialPageLoader/InitialPageLoader';
import { TicketCustomFormSection } from '../../shared/components/TicketCustomFormTab/TicketCustomFormSection';
import { CustomFormUploadProps, formatInputRawData, mapCardInputToAnswers, parseCard, updateRequestValue } from '../../shared/components/TicketTemplateAnswer';
import { Toolbar } from '../../shared/components/Toolbar';
import { graphService } from '../../shared/services/graph.service';
import { platformService } from '../../shared/services/platform.service';
import { uploadService } from '../../shared/services/upload.service';
import { FILTERS_STRINGS, markdownComponents, STATUS_GUID } from '../../shared/utils/constants';
import { parseIfJSON } from '../../shared/utils/helper';
import { AdaptiveCardElement } from '../../templates/AdaptiveCardElement';
import { AddComment } from '../../templates/CustomComponents/AddComment';
import { RequestTitle } from '../../templates/CustomComponents/RequestTitle';
import { UploadFile, UploadFileProps } from '../../templates/CustomComponents/UploadFile';
import { CustomForm, CustomFormAttachmentsProps } from '../../templates/CustomForms/CustomForm.Interface';
import { markFormsAsUnanswerd, parseCardTemplate, setCustomFormTemplateData, updateCustomFormListWithAnswers, validateRequiredInputs } from '../../templates/CustomForms/CustomForm.Service';
import { getPortalSource, getUserFromToken } from '../ticketHelper';
import useActionButtonsFlex from '../useActionButtonsFlex';
import { CatalogImage } from '../../shared/components/Wysiwyg/CatalogImage';
import { ReloadEndUserCFOptions } from './Requests';
import { answeredFormFiles, FileUploadProgress, PortalUploadProgress, startingUploadProgress, uploadFormFiles } from './portalFormUploads';

interface Props {
  fromCatalog?: boolean;
}

const CreateRequest: React.FC<Props> = ({ fromCatalog }) => {

  enum stateType {
    initialPageLoader,
    requestCard,
    dialogReceived,
    markdownAnswer,
    dialogOops,
    dialogCreating,
    archivedArticle,
    dialogGladWeHelped,
    scopeErrorMessage
  };

  const { kbId, type, itemId } = useParams<any>();
  const { t } = useTranslation();
  const currentState = appState();
  const isAgentOnly = currentState?.userRoles?.isAuthorizedAgent ?? false;
  const [state, setState] = useState(stateType.initialPageLoader);
  const [scopeErrorType, setScopeErrorType] = useState("Template");
  const [request, setRequest] = useState("");
  const [answer, setAnswer] = useState("");
  const [templateResponse, setTemplateResponse] = useState({});
  const [dataSourceList, setDataSourceList] = useState([]);
  const [isRenderComplete, setIsRenderComplete] = useState(false);
  const [customForms, setCustomForms] = useState<CustomForm[]>([]);
  const [activeForm, setActiveForm] = useState<CustomForm>(null);
  const [currentTemplate, setCurrentTemplate] = useState<any>({});
  const [isSummaryPage, setIsSummaryPage] = useState(false);
  const [isResetForm, setIsResetForm] = useState(false);
  const [goToNextForm, setGoToNextForm] = useState(false);
  const [continueButtonText, setContinueButtonText] = useState("");
  const [showSummaryCard, setShowSummaryCard] = useState(false);
  const [navs, setNavs] = useState<Breadcrumbs>();
  const history = useHistory();
  const actionsFlexProps = useActionButtonsFlex();
  const api = new platformService();
  const uploadHelper = new uploadService();
  const [customFormListAttachmentsProps, setCustomFormListAttachmentsProps] = useState<CustomFormAttachmentsProps[]>([]);
  const [customFormExtraAttachmentsProps, setCustomFormExtraAttachmentsProps] = useState<CustomFormUploadProps>();
  const [isCustomFormLoading, setIsCustomFormLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress[]>([]);
  const [isMovingToNextForm, setIsMovingToNextForm] = useState(false);



  const submitTicket = async () => {
    try {

      let preventSubmit = false;
      if (state == 1)
        preventSubmit = CreateRequest_TriggerExecuteButton();

      if (preventSubmit) return;

      const formFiles = answeredFormFiles(customFormListAttachmentsProps, customForms.filter(x => x.IsAnswered));
      setUploadProgress(startingUploadProgress(formFiles));
      setState(stateType.dialogCreating);

      const requester = await getUserFromToken();
      let source = await getPortalSource();

      const ticketData: any = {
        Title: request,
        RequesterId: requester.Id,
        Requester: { EmailAddress: (requester.Email ?? requester.UserName), AadObjectId: requester.AadObjectId, UserPrincipalName: requester.UserName },
        TemplateId: parseFloat(itemId),
        SourceId: source.Id
      };
      if (itemId && !templateResponse[UploadFile.ElementId]) {
        ticketData.CardAnswerJson = JSON.stringify(templateResponse);
        ticketData.CardTemplateJson = RemoveRequest_ActionSet(answer, t);
      }
      ticketData.CardAnswerJson = FormatCardAnswerJson(ticketData.CardAnswerJson, ticketData.CardTemplateJson);
      if (ticketData.Title.trim() === "" && (templateResponse["Ticket.Request"]?.trim() ?? "") !== "")
        ticketData.Title = templateResponse["Ticket.Request"];

      let customFormList = [];
      customForms.filter(x=>x.IsAnswered).forEach(form =>{
        const cardAnswer = JSON.stringify(form.CardAnswerJson);
        const cardAnswerObj = FormatCardAnswerJson(cardAnswer, JSON.stringify(form.CardTemplateJson));
        customFormList.push({ TemplateFormId: form.TemplateFormId, FormName: form.Name, Order: form.Order, CardTemplateJson: JSON.stringify(form.CardTemplateJson), CardAnswerJson: cardAnswerObj });
      });
      if(customFormList.length>0)
        ticketData.CustomForms = customFormList;

      const r = await api.addRequest(ticketData);
      const ticket = r.data;
      if (ticket && formFiles.length > 0)
        await uploadFormFiles(api, uploadHelper, ticket.Id, formFiles, setUploadProgress);

      history.push(`/${isAgentOnly ? 'tickets' : 'requests'}/${ticket.Id}`);
    }
    catch (e) {
      console.error(e);
      setState(stateType.dialogOops);
    }
  };
  const deflectTicket = async () => {
    try {
      const requester = await getUserFromToken();
      let source = await getPortalSource();
      let status = await api.getStatus(`?$filter=Guid eq ${STATUS_GUID.DEFLECTED}`);

      const ticket: any = {
        Title: request,
        RequesterId: requester.Id,
        Requester: { EmailAddress: (requester.Email ?? requester.UserName), AadObjectId: requester.AadObjectId, UserPrincipalName: requester.UserName },
        TemplateId: parseFloat(itemId),
        SourceId: source.Id,
        StatusId: status.data.value[0]?.Id,
        ResolutionDate: new Date()
      };

      // Create the ticket asynchronously without them knowing for a better experience
      (async () => {
        try {
          const r = await api.addRequest(ticket);
          await api.addCommentToTicket(r.data.Id, { Body: `Deflected article: https://${window.location.host}/requestcatalog/${itemId}/article/${itemId} \n\n ${answer}`, IsPublic: true, ModifiedById: 0 });
        } catch {
          // If this fails, it doesn't matter, we just won't created the deflected ticket
        }
      })();
    } catch {
      // If this fails to create a deflected ticket, just skip it and go back to the catalog
    }

    setState(stateType.dialogGladWeHelped);
    setTimeout(() => history.push(`/requests`), 4000);
  }

  const getTemplate = async (templateId) => {
    try {
      let showScopeError = false;
      const result = { Answer: "", Question: "", Archived: false };
      const temp = await api.getTicketTemplates(`?$filter=Id eq ${templateId}&v=${new Date().getTime()}&$expand=TemplateForms($expand=Form)`);

      let templateData = null;
      if (temp.data.value.length > 0) {
        templateData =  temp.data.value[0];
      }
      
      if (fromCatalog && kbId) {
        const ans = await api.getTemplateArticle(kbId);
        if (ans.data.AnswerId==0) {
          showScopeError = true;
          setState(stateType.scopeErrorMessage);
        }

        result.Answer = ans.data.Answer;
        result.Question = ans.data.Question;
        // ans.data.IsArchived only reflects the KB offering/answer, not the template it links
        // to - a template archived on its own (independent of the catalog offering) must still
        // block the request.
        result.Archived = ans.data.IsArchived || (templateData?.IsArchived ?? false);
        setNavs({
          breadcrumbs: [
            { title: t('requests.service-catalog'), link: '/requests' },
            { title: ans.data.ServiceOffering, link: '/requests' },
            { title: ans.data.RequestOffering, link: '' }
          ]
        });
      } else {
        try
        {
          const { data } = await api.isTemplateAccessDenied(templateId);
          if (data.value === true) {
            setScopeErrorType("Template");
            setState(stateType.scopeErrorMessage);
            showScopeError = true;
          } else {
            result.Answer =  (templateData.TemplateForms.length > 0) ? templateData.TemplateForms[0].Form.Definition : templateData.CardResponseJson;
            result.Question = templateData.Name;
            result.Archived = templateData.IsArchived;
          }
        }
        catch
        {
          setState(stateType.scopeErrorMessage);
          showScopeError = true;
        }
      }

      if (!showScopeError) {
        if (result.Archived ?? true) {
          // Only CreateRequestAdaptiveCard's onLoad ever flips isRenderComplete to true, and it
          // never mounts for this state, so without this the InitialPageLoader spins forever
          // over a Card stuck at visibility: hidden.
          setIsRenderComplete(true);
          setState(stateType.archivedArticle);
        } else if (result.Answer) {
          const dsResult = await api.getDataSources(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}`);
          const dsList = dsResult.data.value;
          setDataSourceList(dsList);

          const defaultTemplate = result.Answer;
          let cardPayload = JSON.parse(defaultTemplate);
          cardPayload.body.push(CreateRequest_ActionSet(t));

          if(templateData?.TemplateForms.length > 0){
            setCustomFormTemplateData(templateData, setCurrentTemplate, setCustomForms, setActiveForm, currentState, dataSourceList);
            const answerTemplateForm = templateData.TemplateForms[0];
            cardPayload = JSON.parse(parseCardTemplate(answerTemplateForm.Form.Definition, JSON.parse("{}"), currentState, dataSourceList, customFormExtraAttachmentsProps));

            setContinueButtonText(answerTemplateForm.SubmitButtonText);
          } 
          
          if (cardPayload)
              setAnswer(JSON.stringify(cardPayload));

          setRequest(result.Question);
          setState(stateType.requestCard);
          setShowSummaryCard(templateData.SummaryCardEnabled);
        } else {
          setIsRenderComplete(true);
          setState(stateType.dialogOops);
        }
      }

    } catch {
      setIsRenderComplete(true);
      setState(stateType.dialogOops);
    }
  };


  const getKnowledge = async (answerId) => {
    try {
      setIsRenderComplete(true);
       const result = await api.getKnowledgeArticle(answerId);

       if (result.data.AgentOnly){
        setScopeErrorType("Kb");
        setState(stateType.scopeErrorMessage);
       }
       else
       {
        setNavs({
          breadcrumbs: [
            { title: t('requests.service-catalog'), link: '/requests' },
            { title: result.data.ServiceOffering, link: '/requests' },
            { title: result.data.RequestOffering, link: '' }
          ]
        });

        if (result.data.Answer=="answer is scoped"){
          setScopeErrorType("Kb");
          setState(stateType.scopeErrorMessage);
        }
        else
        {
          setAnswer(result.data.Answer);
          setRequest(`Service Catalog Deflection: ${result.data.Question}`);
          setState(stateType.markdownAnswer);
        }
       }
    } catch {
      setState(stateType.dialogOops);
    }
  };

  const updateActiveFormAnswer = () => {
    const mergedAnswers = Object.assign(activeForm.CardAnswerJson, templateResponse);
    setActiveForm(preState => ({
        ...preState,
        CardAnswerJson: mergedAnswers
      }));
  }

  const startOverForm = async()=>{
    setIsResetForm(true);
    setCustomForms(preState => ([]));
    setCustomFormTemplateData(currentTemplate, setCurrentTemplate, setCustomForms, setActiveForm, currentState, dataSourceList);
  }

  const resetForm = () => {
    const nextForm = customForms[0];
    const cardPayload = JSON.parse(parseCardTemplate(nextForm.CardTemplateJson, nextForm.CardAnswerJson, currentState, dataSourceList, customFormExtraAttachmentsProps));
    
    setActiveForm(nextForm);
    setIsSummaryPage(false);
    setContinueButtonText(nextForm.SubmitButtonText);

    if(cardPayload)
      setAnswer(JSON.stringify(cardPayload));

    setIsResetForm(false);
  };

  const redirectToNextForm = async() =>{
    try {
      // A template with one form has nowhere to go next, so asking the server only holds up the submit.
      const result = customForms.length > 1 ? await api.executeFormTransition(itemId, activeForm.TemplateFormId, activeForm,0) : null;
      const nextTemplateForm = result?.data ?? null;

      if(nextTemplateForm){
        const nextForm = customForms[nextTemplateForm.Order] ?? null;
        const cardTemplate = JSON.stringify(nextForm.CardTemplateJson);
        const cardAnswers = nextForm.CardAnswerJson;
        const cardPayload = parseCardTemplate(cardTemplate, cardAnswers, currentState, dataSourceList, customFormExtraAttachmentsProps);

        setContinueButtonText(nextTemplateForm.SubmitButtonText);
        setActiveForm(nextForm);

        if(cardPayload)
          setAnswer(cardPayload);
      }else{
        setIsSummaryPage(true);
        if(!showSummaryCard)
          submitTicket();
      }
    } finally {
      setIsMovingToNextForm(false);
    }
  }

  const moveToNextForm = async () =>{
    const currentTemplate = parseIfJSON(activeForm.CardTemplateJson);
    const currentAnswer = parseIfJSON(templateResponse);
    const validInputs = validateRequiredInputs(currentTemplate, currentAnswer, currentState?.customProperties ?? []);

    if(validInputs)
    {
      setIsMovingToNextForm(true);
      updateActiveFormAnswer();
      setGoToNextForm(true);
      setTemplateResponse({});
    }
  }

  const moveToPreviousForm = async()=>{
    const allAnsweredForms = customForms.filter(x=>x.IsAnswered);
    const lastAnsweredIndex = _.findLastIndex(allAnsweredForms, x=>x.TemplateFormId==activeForm.TemplateFormId);
    const prevFormIndex = (!isSummaryPage) ? lastAnsweredIndex-1: (allAnsweredForms.length-1);
    const nextForm = allAnsweredForms[prevFormIndex];
    const cardPayload = JSON.parse(parseCardTemplate(nextForm.CardTemplateJson, nextForm.CardAnswerJson, currentState, dataSourceList, customFormExtraAttachmentsProps));
    
    setActiveForm(nextForm);
    setIsSummaryPage(false)
    setContinueButtonText(nextForm.SubmitButtonText);
    setTemplateResponse(nextForm.CardAnswerJson);
    setIsCustomFormLoading(true);
    
    if(cardPayload)
      setAnswer(JSON.stringify(cardPayload));

    markFormsAsUnanswerd(allAnsweredForms, customForms, lastAnsweredIndex, setCustomForms);
  }
  

  useEffect(()=>{
    if(isResetForm){
      resetForm();
    }
  },[customForms]);

  useEffect(()=>{
    if(activeForm?.TemplateFormId)
      setCustomFormExtraAttachmentsProps({activeFormTemplateId: activeForm?.TemplateFormId})
    if(!isResetForm)
      updateCustomFormListWithAnswers(customForms, activeForm, setCustomForms);

    if(goToNextForm){
      const moveToNextForm = async() => { await redirectToNextForm(); };
      moveToNextForm();
      setGoToNextForm(false);
    }
      
  },[activeForm]);

  useEffect(() => {
    if (type === 'template')
      getTemplate(itemId);
    else
      getKnowledge(itemId);
  }, []);

  

  return (<ProviderConsumer render={(globalTheme) => (
    <>
      {fromCatalog && <Toolbar globalTheme={globalTheme} title={t('requests.service-catalog')} hideSave={true} breadcrumbs={navs} forceBreadcrumbs={true} />}
      <div className={fromCatalog ? 'h-catalog-entry' : 'h-screen'}>
        {(state === stateType.initialPageLoader) && (<InitialPageLoader />)}
        {state !== stateType.initialPageLoader && (<>
          {state !== stateType.scopeErrorMessage && (<>
            {!isRenderComplete && (<InitialPageLoader />)}
            <Card className="mobile-tab-padding" style={{ width: '100%', overflowY: 'scroll', visibility: isRenderComplete ? 'visible' : 'hidden' }}>
              {state === stateType.requestCard && (
                <div className='email-request-card'>
                  <Card.Body>
                    {!isSummaryPage && <CreateRequestAdaptiveCard request={" "} isEndUserPage={!isAgentOnly} cardTemplate={answer} setCardTemplate={setAnswer} templateResponse={templateResponse} setTemplateResponse={setTemplateResponse} dataSourceList={dataSourceList} setRenderComplete={setIsRenderComplete} setCustomFormListAttachmentsProps={setCustomFormListAttachmentsProps} activeFormTemplateId={activeForm?.TemplateFormId} isCustomFormLoading={isCustomFormLoading} />}
                    {isSummaryPage && <TicketCustomFormSection customForms={customForms.filter(x=>x.IsAnswered)} disabledFormInputs={true} template={currentTemplate} showSummaryInfo={true} isEndUserPage={!isAgentOnly}/>}
                  </Card.Body>
                  <Card.Footer>
                    <Flex gap='gap.small' className={'mt-4'} {...actionsFlexProps}>
                      {fromCatalog && <Button content={t('common.buttons.cancel')} onClick={() => { history.push('/requests'); }} style={{ marginRight: 2 }}/>}
                      {(isSummaryPage || (activeForm.Order > 0)) && <Button content={t('common.buttons.back')} onClick={moveToPreviousForm} style={{ marginRight: 2 }} />}
                      {isSummaryPage && <Button  content={t('common.buttons.start-over')} onClick={startOverForm} style={{ marginRight: 2 }} />}     
                      {!isSummaryPage && <Button primary content={continueButtonText} onClick={moveToNextForm} loading={isMovingToNextForm} disabled={isMovingToNextForm} style={{ marginRight: 2 }} />}
                      {isSummaryPage && <Button primary content={t('common.buttons.submit')} onClick={submitTicket} style={{ marginRight: 0 }} />}
                    </Flex>
                  </Card.Footer>
                </div>
              )}
              {state === stateType.dialogReceived && (<div className='text-center'>{t('requests.dialog.received')}  {t('requests.dialog.close-window')}</div>)}
              {state === stateType.markdownAnswer && (
                <div className='mx-auto'>
                  <Card elevated className='min-w-1/2-screen h-unset-important card-border-accent'>
                    <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={{ ...markdownComponents, img: CatalogImage }}>{DOMPurify.sanitize(answer)}</Markdown>
                  </Card>
                  {fromCatalog && (<>
                    <div className='text-center pt-6'>{t('requests.article-helpful')}</div>
                    <Flex gap='gap.small' className={'mt-4'} hAlign='center'>
                      <Button primary content={t('common.buttons.yes')} onClick={deflectTicket} />
                      <Button content={t('common.buttons.no')} onClick={() => { history.push('/requests'); }} style={{ marginRight: 0 }} />
                    </Flex>
                  </>)}
                </div>)}
              {state === stateType.dialogOops && (<div className='text-center'>{t('requests.dialog.oops')}</div>)}
              {state === stateType.dialogCreating && (uploadProgress.length > 0
                ? <PortalUploadProgress files={uploadProgress} />
                : <div className='text-center'>{t('requests.dialog.create-with-close')}</div>)}
              {state === stateType.archivedArticle && (<div className='text-center'>{t('requests.dialog.archived-article')}</div>)}
              {state === stateType.dialogGladWeHelped && (<div className='text-center'>{t('requests.dialog.glad-we-helped')}</div>)}
            </Card></>)
          }
          {state === stateType.scopeErrorMessage && (<ScopeErrorMessage msgType={scopeErrorType} />)}
        </>)}
      </div>
    </>)} />);
};

export default CreateRequest;

export const CreateRequest_ActionSet = (t) => {
  return {
    type: 'ActionSet',
    actions: [
      {
        type: 'Action.Submit',
        title: t('ticket-details.edit-ticket-form.custom-form-answer.button.update-answer'),
        data: {
          commandId: 'CreateTicketFromTemplate'
        }
      }
    ]
  };
};

export const RemoveRequest_ActionSet = (cardAnswer: string, t) => {
  const actionSet = JSON.stringify(CreateRequest_ActionSet(t));
  const replaceStr = cardAnswer.replace(`,${actionSet}`, "");
  return replaceStr;
};

export const CreateRequest_TriggerExecuteButton = () => {
  const parent = document.querySelector(".AdaptiveCardElementValidationClass");
  if (!parent) return;
  const field = parent.querySelectorAll(".ac-pushButton");
  if (field.length > 0) {
    const btn = field[field.length - 1] as HTMLButtonElement;
    btn.click();
    const validationFailed = parent.querySelector('.ac-input-validation-failed');

    return (validationFailed?.nodeName ?? "").length > 0;
  }
  return false;
}

export const IsGenericInputsExist = (templateResponse: any) => {
  return Object.keys(templateResponse)?.some(x => !x.startsWith("Ticket.")) ?? false;
}

export const FormatCardAnswerJson = (cardAnswerJson: string, cardTemplateJson: string, stringify: boolean = true) => {
  if (cardAnswerJson && cardAnswerJson.length > 0 && cardTemplateJson && cardTemplateJson.length > 0) {
    let cardFormAnswersObj = JSON.parse(cardAnswerJson);
    formatInputRawData(JSON.parse(cardTemplateJson)?.body, cardFormAnswersObj);
    return stringify ? JSON.stringify(cardFormAnswersObj) : cardFormAnswersObj;
  }

  return stringify ? cardAnswerJson : JSON.parse(cardAnswerJson);
}

interface RequestAcProps {
  cardTemplate: string;
  setCardTemplate: React.Dispatch<React.SetStateAction<string>>;
  templateResponse: any;
  setTemplateResponse: React.Dispatch<React.SetStateAction<any>>;
  request?: string;
  isEndUserPage?: boolean;
  dataSourceList: any[];
  setRenderComplete?: any;
  setCustomFormListAttachmentsProps: React.Dispatch<React.SetStateAction<CustomFormAttachmentsProps[]>>;
  activeFormTemplateId: number;
  isCustomFormLoading?: boolean;
}

export const CreateRequestAdaptiveCard = ({ cardTemplate, setCardTemplate, templateResponse, setTemplateResponse, request, isEndUserPage, dataSourceList, setRenderComplete, setCustomFormListAttachmentsProps, activeFormTemplateId, isCustomFormLoading }: RequestAcProps) => {
  const { t } = useTranslation();
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [customFormAttachmentsProps, setCustomFormAttachmentsProps] = useState<CustomFormAttachmentsProps>({} as CustomFormAttachmentsProps);
  const [isLoading, setIsLoading] = useState(true);
  const setAppState = useSetState();
  const currentState = appState();
  const graphAPI = new graphService();
  const api = new platformService();

  useEffect(() => {
    if(customFormAttachmentsProps?.TemplateFormId)
      setCustomFormListAttachmentsProps(prev => {
        let idFound = false;
        let newItems = prev.map(item => {
          if(item.TemplateFormId === activeFormTemplateId){
            item = Object.assign(item, customFormAttachmentsProps);
            idFound = true;
          }
          return item;
        });

        if(!idFound){
          const newItem = {TemplateFormId: activeFormTemplateId, ...customFormAttachmentsProps } as CustomFormAttachmentsProps;
          newItems.push(newItem);
        }
        return newItems;
      });

  }, [customFormAttachmentsProps]);

  const updateCardPayload = () => {
    const cardPayload = JSON.parse(cardTemplate);

    if (cardPayload) {
      const thistemplateResponse = (!isLoading || isCustomFormLoading) ? templateResponse : {};
      parseCard(cardPayload.body, thistemplateResponse, currentState, {}, dataSourceList);
      updateRequestValue(cardPayload.body, thistemplateResponse, currentState, request);
      setCardTemplate(JSON.stringify(cardPayload));
      setTemplateResponse(thistemplateResponse);
    }
  }

  const onloadCardPayload = (onLoadUpdatedInputs) => {
    const thistemplateResponse = templateResponse;
    Object.assign(thistemplateResponse, onLoadUpdatedInputs);
    setTemplateResponse(thistemplateResponse);
    setIsLoading(false);
    if (setRenderComplete) setRenderComplete(true);
  }

  useEffect(() => {
    (async () => {
      if ((currentState?.usersInRole?.length ?? 0) == 0)
        await ReloadEndUserCFOptions(api, graphAPI, currentState, setAppState);
      updateCardPayload();
    })();
  }, [currentState?.usersInRole]);

  const setPayloadData = (cardPayload, cardAnswerJson) => {
    const extraProps: CustomFormUploadProps = {
      setUploadFiles: setUploadFiles,
      activeFormTemplateId: activeFormTemplateId,
      setCustomFormAttachmentsProps: setCustomFormAttachmentsProps
    }

    parseCard(cardPayload.body, cardAnswerJson, currentState, extraProps, dataSourceList);

    if (cardPayload)
      setCardTemplate(JSON.stringify(cardPayload));

    setTemplateResponse(cardAnswerJson);
  }

  const onCloseUploadFromCardAction = (currentFiles?: FileProps[], optionalMessage?: string, isPrivate?: boolean) => {
    setOpenUploadDialog(false);
    if (currentFiles?.length > 0) {

      const thistemplateResponse = templateResponse;
      thistemplateResponse[UploadFile.ElementId] = JSON.stringify({ Files: currentFiles.map(x => { return { FileName: x.FileName }; }), Comment: { Body: optionalMessage, IsPublic: !isPrivate } } as UploadFileProps);
      setTemplateResponse(thistemplateResponse);

      const cardPayload = JSON.parse(cardTemplate);
      setPayloadData(cardPayload, thistemplateResponse);

      const newFormAttachmentProps: CustomFormAttachmentsProps = {TemplateFormId: activeFormTemplateId, Files: currentFiles, Message: optionalMessage, IsPrivate: isPrivate};
      setCustomFormAttachmentsProps(newFormAttachmentProps);

    }
  }

  return (<ProviderConsumer
    render={(globalTheme) => (
      <>
        <AdaptiveCardElement jsonString={cardTemplate} cardProps={{ styles: { height: 'fit-content' } }}
          requestMappedText={request}
          onInputValueChanged={(input: Input) => {
            setTemplateResponse(cardResponse => {
              let inputId = mapCardInputToAnswers(input, cardResponse);
              const cardPayload = JSON.parse(cardTemplate);
              if (cardPayload && ![AddComment.ElementId,RequestTitle.ElementId].includes(inputId)) {
                updateRequestValue(cardPayload.body, cardResponse, currentState, request);
              }

              return cardResponse;
            });
          }}
          uploadFileProps={{
            key: "CreateRequest", currentFiles: customFormAttachmentsProps?.Files,
            onOpenUploadFromCardAction: () => {
              setOpenUploadDialog(!openUploadDialog);
            }
          }}
          onLoad={onloadCardPayload}
          isEndUserPage={isEndUserPage}
        />
        <Dialog
          closeOnOutsideClick={false}
          open={openUploadDialog}
          content={<NewAttachmentFlowContent globalTheme={globalTheme} ticketId={0} filesToUpload={uploadFiles} onInteraction={() => { }} teamsConfig={currentState.teamsConfig} isEndUserPage={isEndUserPage} onClose={onCloseUploadFromCardAction} uploadFromCustomFormProps={{ fromTemplate: true, attachToCustomFormFromWeb: true }} />}
          header={t('ticket-details.edit-ticket-form.custom-form-answer.addto-customform')}
          headerAction={{
            icon: <CloseIcon />,
            title: t('common.buttons.close'),
            onClick: () => setOpenUploadDialog(false)
          }}
          style={{ maxWidth: '700px' }}
          className="responsive-dialog top-0 fixed md:relative z-10 mobile-full-p md:w-attachment"
        />
      </>
    )} />);
};
